import { IsNullOrUndefinedOrNaN } from './Helpers';

export function addCommas(num: number | string | undefined, numFormatOptions?: Intl.NumberFormatOptions): string {
  if ((typeof num === 'string' && num.trim() === '') || num === undefined) {
    return '';
  }

  if (isNaN(Number(num))) {
    return '' + num;
  }
  return Number(num).toLocaleString(undefined, numFormatOptions);
}

export function removeCommas(num: string | number | null): string | number | null {
  if (typeof num === 'string') {
    num = num.replaceAll(',', '');
  }
  return num;
}

export function isNumeric(num: number | string | null): boolean {
  return num !== undefined && num != null && (typeof num === 'number' || num.trim() !== '') && !isNaN(num as number);
}

export function compactNumber(
  value: number | null | undefined | string,
  options: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 1 },
  levels = ['T', 'B', 'M', 'K'],
  compactDecimal = false,
): string {
  if (value === undefined || value === null) {
    return '--';
  }
  if (typeof value === 'string') {
    return compactNumber(Number(value));
  }
  if (isNaN(value) || !isFinite(value)) {
    return '--';
  }

  const absVal = Math.abs(value);
  let compactStr: string;

  if (absVal > 999_999_999_999 && levels.includes('T')) {
    compactStr = (absVal / 1_000_000_000_000).toLocaleString('en-US', options) + 'T';
  } else if (absVal > 999_999_999 && levels.includes('B')) {
    compactStr = (absVal / 1_000_000_000).toLocaleString('en-US', options) + 'B';
  } else if (absVal > 999_999 && levels.includes('M')) {
    compactStr = (absVal / 1_000_000).toLocaleString('en-US', options) + 'M';
  } else if (absVal > 999 && levels.includes('K')) {
    compactStr = (absVal / 1_000).toLocaleString('en-US', options) + 'K';
  } else {
    const digits = compactDecimal
      ? { minimumFractionDigits: 1, maximumFractionDigits: 1 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    const str = absVal.toLocaleString('en-US', { ...options, ...digits });
    if (str.endsWith('.00')) {
      compactStr = str.slice(0, -3);
    } else if (str.endsWith('.0')) {
      compactStr = str.slice(0, -2);
    } else {
      compactStr = str;
    }
  }

  return `${value < 0 ? '-' : ''}${compactStr}`;
}

export function uncompactNumber(value: string): number {
  value = value || '0';
  const trimmed = value.replace(/[$,%]/g, '').trim();

  if (!trimmed) {
    return 0;
  }

  if (!isNaN(Number(trimmed.slice(-1)))) {
    const num = Number(trimmed);
    return isNaN(num) ? 0 : num;
  }

  const num = Number(trimmed.slice(0, -1));

  if (isNaN(num)) {
    return 0;
  }

  switch (trimmed.slice(-1).toUpperCase()) {
    case 'T':
      return num * 1_000_000_000_000;
    case 'B':
      return num * 1_000_000_000;
    case 'M':
      return num * 1_000_000;
    case 'K':
      return num * 1_000;
    default:
      return num;
  }
}

export function percentage(
  value: number | null | undefined | string,
  minimumFractionDigits = 0,
  maximumFractionDigits = 1,
): string {
  if (value === undefined || value === null) {
    return '--';
  }
  if (typeof value === 'string') {
    return percentage(Number(value));
  }
  if (isNaN(value) || !isFinite(value)) {
    return '--';
  }
  return value.toLocaleString('en-US', { minimumFractionDigits, maximumFractionDigits }) + '%';
}

export function toPercentage(value: number, numFormatOptions?: Intl.NumberFormatOptions): string {
  return value.toLocaleString('en-US', { style: 'percent', ...numFormatOptions });
}

export function toCurrency(value: number, numFormatOptions?: Intl.NumberFormatOptions): string {
  return addCommas(value, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...numFormatOptions,
  });
}

export function moneyScale(code: string | null | undefined): number {
  if (code === undefined || code === null) {
    return 1;
  }

  switch (code) {
    case 'M':
      return 1_000_000;
    case 'B':
      return 1_000_000_000;
    case 'T':
      return 1_000_000_000_000;
    default:
      return 1;
  }
}

export function calcAverage(data: number[]): number {
  const sum = data.filter((x) => !IsNullOrUndefinedOrNaN(x)).reduce((a, b) => a + b, 0);
  return sum / data.length || 0;
}

export function calcMedian(data: number[]): number {
  const sorted = data.filter((x) => !IsNullOrUndefinedOrNaN(x)).sort((a, b) => a - b);
  if (sorted.length % 2 === 0) {
    const a = sorted[sorted.length / 2 - 1];
    const b = sorted[sorted.length / 2];
    return (a + b) / 2;
  } else {
    const mid = Math.floor(sorted.length / 2);
    return sorted[mid];
  }
}

export function romanToArabic(romanNumber: string | null | undefined): number {
  if (romanNumber === undefined || romanNumber === null) {
    return 0;
  }
  romanNumber = romanNumber.toUpperCase();
  const romanNumList = ['CM', 'M', 'CD', 'D', 'XC', 'C', 'XL', 'L', 'IX', 'X', 'IV', 'V', 'I'];
  const corresp = [900, 1000, 400, 500, 90, 100, 40, 50, 9, 10, 4, 5, 1];
  let index = 0,
    num = 0;
  for (const rn in romanNumList) {
    index = romanNumber.indexOf(romanNumList[rn]);
    while (index != -1) {
      num += corresp[rn];
      romanNumber = romanNumber.replace(romanNumList[rn], '-');
      index = romanNumber.indexOf(romanNumList[rn]);
    }
  }
  return num;
}

export function parseIntOrDefault<T>(val: unknown, defaultVal: T): number | T {
  if (!val) {
    return defaultVal;
  } else if (typeof val === 'string') {
    return parseInt(val);
  } else if (typeof val === 'number') {
    return val;
  }
  return defaultVal;
}

export function parseFloatOrDefault<T>(val: unknown, defaultVal: T): number | T {
  if (!val) {
    return defaultVal;
  } else if (typeof val === 'string') {
    return parseFloat(val);
  } else if (typeof val === 'number') {
    return val;
  }
  return defaultVal;
}
